import { DEV_HOSTS } from "../constants/hosts";

export type LoggerLevels =
	| "error"
	| "warn"
	| "info"
	| "http"
	| "verbose"
	| "debug"
	| "silly";

const LoggerLevelsArray: LoggerLevels[] = [
	"error",
	"warn",
	"info",
	"http",
	"verbose",
	"debug",
	"silly",
];

const compatibilityLevels = {
	error: console.error,
	warn: console.warn,
	info: console.info,
	log: console.log,
	http: console.log,
	verbose: console.log,
	debug: console.debug,
	silly: console.log,
};

const generateLevelInput = (level: string, size = 5) =>
	`${Array.from({ length: size - level.length }, () => " ").join(
		"",
	)}${level}`;

type LoggerMessage = {
	level: LoggerLevels;
	message: any[];
	meta: {
		level: string[];
		[x: string]: any;
	};
};

function consoleLogTransport(info: LoggerMessage): void {
	compatibilityLevels[info.level](
		`(${new Date().toLocaleString()}) ${generateLevelInput(
			info.level,
		)} [${info.meta.level.join(" > ")}]:`,
		...info.message,
	);
}

export type LoggerOptions = {
	name?: string;
	level?: LoggerLevels;
	callback?: (info: LoggerMessage) => void | Promise<void>;
	parentLogger?: Logger;
};

class Logger {
	public name: string;

	public nameLevel: string[];

	public callback: (info: LoggerMessage) => void | Promise<void>;

	public level: LoggerLevels;

	constructor(options?: LoggerOptions) {
		const opts = options || {};
		if (opts.parentLogger) {
			this.name = opts.name ?? "";
			this.nameLevel = [...opts.parentLogger.nameLevel, this.name];
			this.level = opts.parentLogger.level;
			this.callback = opts.parentLogger.callback.bind(opts.parentLogger);
		} else {
			this.level = opts.level ?? "info";
			this.callback = opts.callback ?? (() => {});
			this.name = opts.name ?? "";
			this.nameLevel = [this.name];
		}
	}

	private execute(level: LoggerLevels, ...message: any[]) {
		if (
			LoggerLevelsArray.indexOf(level) <=
			LoggerLevelsArray.indexOf(this.level)
		) {
			this.callback({
				level,
				message,
				meta: {
					level: this.nameLevel,
				},
			});
		}
	}

	public error(...message: any[]) {
		this.execute("error", ...message);
	}

	public warn(...message: any[]) {
		this.execute("warn", ...message);
	}

	public info(...message: any[]) {
		this.execute("info", ...message);
	}

	public log(...message: any[]) {
		this.execute("info", ...message);
	}

	public http(...message: any[]) {
		this.execute("http", ...message);
	}

	public verbose(...message: any[]) {
		this.execute("verbose", ...message);
	}

	public debug(...message: any[]) {
		this.execute("debug", ...message);
	}

	public silly(...message: any[]) {
		this.execute("silly", ...message);
	}

	public trace(...message: any[]) {
		this.execute("silly", ...message);
	}
}

const isProduction = process.env.NODE_ENV === "production";
const LOG_LEVEL = isProduction ? "info" : "silly";

const isLocalhost = DEV_HOSTS.includes(window.location.hostname);

/**
 * Hot subscriptions log on every event batch, which is enough to freeze the tab
 * under load. `localStorage.setItem("logs", "off")` mutes them without a rebuild.
 */
const isInfoMuted = (() => {
	try {
		return window.localStorage.getItem("logs") === "off";
	} catch {
		return false;
	}
})();

const createLogger = (options: LoggerOptions) => {
	const logger = new Logger({
		level: LOG_LEVEL,
		callback: consoleLogTransport,
		...options,
	});

	if (!isLocalhost || isInfoMuted) {
		logger.info = () => {};
	}

	return logger;
};

export default createLogger;
